/**
 * Check to chip grouping for the home health strip.
 *
 * DESIGN.md and the part_b mockup fix the strip at one summary pill plus eight compact
 * chips: Store, Checkout, Payments, Shipping, Email, Security, Server, Crons. The API
 * returns a check per underlying probe, currently eighteen of them; rendering one tile per
 * check is what produced the wall of grey. Each chip groups a set of checks and the worst
 * state wins.
 *
 * Two rules that the strip lives or dies by:
 *   - a not_configured check is not an issue and is never aged. Nothing is going to
 *     refresh an input that was never wired up, so ageing it manufactures a permanent
 *     stale warning. Those checks appear once, in the muted footer.
 *   - unknown is not ok. It stays visible on its chip, but it is not counted as an issue
 *     either, because "I could not tell" is not "this is broken".
 */
import type { HealthCheck, HealthState } from '@elutide/shared';

export interface ChipSpec {
  id: string;
  label: string;
  checks: readonly string[];
}

export interface Chip extends ChipSpec {
  state: HealthState;
  value: string | null;
  /** Seconds since the oldest configured check behind the chip ran. Null when none are. */
  ageSec: number | null;
  /** ISO timestamp of the oldest configured check, for AgeStamp. Null when none are. */
  asOf: string | null;
}

/** The eight, in the fixed display order. Mirrors apps/api/src/modules/health/strip.ts. */
export const CHIP_SOURCES: readonly ChipSpec[] = [
  { id: 'store', label: 'Store', checks: ['store', 'wc_rest'] },
  { id: 'checkout', label: 'Checkout', checks: ['checkout'] },
  { id: 'payments', label: 'Payments', checks: ['payment_matcher'] },
  { id: 'shipping', label: 'Shipping', checks: ['shipping'] },
  { id: 'email', label: 'Email', checks: ['postmark'] },
  { id: 'security', label: 'Security', checks: ['wordfence', 'cloudflare'] },
  { id: 'server', label: 'Server', checks: ['disk', 'load', 'db', 'wp_db', 'bridge'] },
  { id: 'crons', label: 'Crons', checks: ['cron', 'ops_jobs', 'outbox_lag', 'cdc_lag', 'tm_sync'] },
];

const RANK: Readonly<Record<HealthState, number>> = {
  not_configured: 0,
  ok: 1,
  unknown: 2,
  warn: 3,
  crit: 4,
};

/**
 * The worst state in a set. not_configured ranks below ok, so a single unwired input never
 * drags a working chip down; a chip reads not_configured only when everything behind it is.
 */
export function worstState(states: readonly HealthState[]): HealthState {
  if (states.length === 0) return 'unknown';
  return states.reduce((worst, s) => (RANK[s] > RANK[worst] ? s : worst));
}

/** True for the states that count towards the issue pill. */
export function isIssue(state: HealthState): boolean {
  return state === 'crit' || state === 'warn';
}

export function buildChips(checks: readonly HealthCheck[], now: Date = new Date()): Chip[] {
  const byName = new Map(checks.map((c) => [c.check, c]));
  return CHIP_SOURCES.map((spec) => {
    const backing = spec.checks
      .map((name) => byName.get(name))
      .filter((c): c is HealthCheck => c !== undefined);
    if (backing.length === 0) {
      return { ...spec, state: 'unknown' as HealthState, value: null, ageSec: null, asOf: null };
    }
    const state = worstState(backing.map((c) => c.state));
    // Show the number from the check driving the state, so an amber chip explains itself.
    const driver = backing.find((c) => c.state === state) ?? backing[0];
    const aged = backing.filter((c) => c.state !== 'not_configured');
    let oldest: HealthCheck | null = null;
    for (const c of aged) {
      const t = Date.parse(c.checked_at);
      if (!Number.isFinite(t)) continue;
      if (oldest === null || t < Date.parse(oldest.checked_at)) oldest = c;
    }
    const ageSec =
      oldest === null ? null : Math.max(0, Math.round((now.getTime() - Date.parse(oldest.checked_at)) / 1000));
    return {
      ...spec,
      state,
      value: driver?.value ?? null,
      ageSec,
      asOf: oldest === null ? null : oldest.checked_at,
    };
  });
}

/** The summary pill: "All systems normal" or "N issues". Only warn and crit count. */
export function summarizeChips(chips: readonly Chip[]): { state: HealthState; label: string } {
  const crit = chips.filter((c) => c.state === 'crit').length;
  const warn = chips.filter((c) => c.state === 'warn').length;
  const issues = crit + warn;
  if (issues === 0) return { state: 'ok', label: 'All systems normal' };
  return { state: crit > 0 ? 'crit' : 'warn', label: `${issues} issue${issues === 1 ? '' : 's'}` };
}

/**
 * Labels of the checks with nothing wired up, for the footer line. Sorted so the line does
 * not reshuffle between refreshes.
 */
export function notConfiguredLabels(checks: readonly HealthCheck[]): string[] {
  return checks
    .filter((c) => c.state === 'not_configured')
    .map((c) => c.label)
    .sort((a, b) => a.localeCompare(b));
}
